import { useState, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, FileSpreadsheet, Check, AlertTriangle, Users, Calendar, Receipt, Package } from "lucide-react";

const IMPORT_TYPES = [
  { key: 'clients', label: 'Clients', icon: Users, endpoint: '/import/clients', color: 'text-pink-500',
    expectedFields: ['Name', 'Email', 'Phone', 'Status', 'Notes', '#Appointments', 'TotalSpent', 'LastAppointment', 'DateAdded', '3WeekAppointmentDate', '4WeekAppointmentDate'] },
  { key: 'appointments', label: 'Appointments', icon: Calendar, color: 'text-blue-500', endpoint: '/import/appointments',
    expectedFields: ['Name', 'Email', 'AppointmentDate', 'Service', 'Price', 'Payment Type', 'Notes', 'BookingStatus', 'BookingID', 'DateBooked'] },
  { key: 'expenses', label: 'Expenses', icon: Receipt, color: 'text-green-500', endpoint: '/import/expenses',
    expectedFields: ['Date', 'Category', 'Net', 'Tax', 'Gross', 'Supplier', 'Notes'] },
  { key: 'inventory', label: 'Inventory', icon: Package, color: 'text-orange-500', endpoint: '/import/inventory',
    expectedFields: ['Name', 'SKU', 'Brand', 'Category', 'Cost', 'Reorder Level', 'Stock'] },
];

export default function ImportWizard() {
  const [activeType, setActiveType] = useState('clients');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const currentType = IMPORT_TYPES.find(t => t.key === activeType);

  const handleFileSelect = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    setMapping({});
    
    // Preview the CSV
    try {
      const text = await f.text();
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 1) return;
      
      // Parse headers
      const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
      
      // Parse preview rows
      const rows = [];
      for (let i = 1; i < Math.min(6, lines.length); i++) {
        const vals = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
        const row = {};
        headers.forEach((h, j) => { row[h] = vals[j] || ''; });
        rows.push(row);
      }
      
      setPreview({ headers, rows });
      
      // Auto-map fields
      const autoMap = {};
      currentType.expectedFields.forEach(ef => {
        const match = headers.find(h => 
          h.toLowerCase().replace(/[\s_#]/g, '') === ef.toLowerCase().replace(/[\s_#]/g, '') ||
          h.toLowerCase().includes(ef.toLowerCase())
        );
        if (match) autoMap[ef] = match;
      });
      setMapping(autoMap);
    } catch (err) {
      
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (Object.keys(mapping).length > 0) {
        formData.append('mapping', JSON.stringify(mapping));
      }
      
      const res = await axios.post(`${API}${currentType.endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(res.data);
      if (res.data.success) {
        toast.success(`Imported ${res.data.imported} records`);
      } else {
        toast.error(res.data.error || 'Import failed');
      }
    } catch (e) {
      toast.error('Import failed: ' + (e.response?.data?.detail || e.message));
    }
    setImporting(false);
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setMapping({});
    setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="space-y-6 fade-in">
      <Toaster position="top-center" />
      <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>Import Data</h1>
      <p className="text-sm text-muted-foreground">Upload CSV files to import clients, appointments, expenses, or inventory.</p>

      {/* Type selector */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {IMPORT_TYPES.map(t => {
          const Icon = t.icon;
          return (
            <Card 
              key={t.key}
              onClick={() => { setActiveType(t.key); setFile(null); setPreview(null); setMapping({}); setResult(null); if (fileRef.current) fileRef.current.value = ''; }}
              className={`p-4 rounded-2xl cursor-pointer transition-colors duration-150 border-2 ${
                activeType === t.key ? 'border-primary bg-secondary/50' : 'border-transparent hover:border-primary/25'
              } shadow-[0_10px_30px_rgba(43,18,27,0.06)]`}
            >
              <Icon size={20} className={t.color} />
              <p className="text-sm font-semibold mt-2" style={{ fontFamily: 'Playfair Display' }}>{t.label}</p>
            </Card>
          );
        })}
      </div>

      {/* Upload area */}
      <Card className="p-6 rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)]">
        <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>Upload {currentType.label} CSV</h3>
        
        {!file && (
          <div 
            className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => fileRef.current?.click()}
          >
            <Upload size={32} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-sm font-medium">Click to upload or drag and drop</p>
            <p className="text-xs text-muted-foreground mt-1">CSV files only</p>
            <input ref={fileRef} type="file" accept=".csv" onChange={handleFileSelect} className="hidden" />
          </div>
        )}

        {file && !result && (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-secondary rounded-xl">
              <div className="flex items-center gap-2">
                <FileSpreadsheet size={18} className="text-primary" />
                <span className="text-sm font-medium">{file.name}</span>
                <Badge variant="secondary" className="text-xs">{(file.size / 1024).toFixed(1)} KB</Badge>
              </div>
              <Button variant="ghost" size="sm" onClick={reset} className="rounded-xl text-xs">Change file</Button>
            </div>

            {/* Preview */}
            {preview && (
              <div>
                <h4 className="text-sm font-semibold mb-2">Preview ({preview.rows.length} rows shown)</h4>
                <div className="overflow-x-auto rounded-xl border">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-muted/30 border-b">
                        {preview.headers.map(h => <th key={h} className="p-2 text-left font-medium">{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((row, i) => (
                        <tr key={i} className="border-b border-border/50">
                          {preview.headers.map(h => <td key={h} className="p-2 max-w-[150px] truncate">{row[h]}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Field mapping */}
            {preview && (
              <div>
                <h4 className="text-sm font-semibold mb-2">Field Mapping</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {currentType.expectedFields.map(ef => (
                    <div key={ef}>
                      <Label className="text-xs">{ef}</Label>
                      <Select 
                        value={mapping[ef] || '__skip__'} 
                        onValueChange={v => setMapping(p => ({...p, [ef]: v === '__skip__' ? '' : v}))}
                      >
                        <SelectTrigger className="mt-1 rounded-xl h-8 text-xs">
                          <SelectValue placeholder="Select column" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__skip__">-- Skip --</SelectItem>
                          {preview.headers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button onClick={handleImport} disabled={importing} className="rounded-xl">
              {importing ? 'Importing...' : `Import ${currentType.label}`}
            </Button>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-3">
            {result.success ? (
              <div className="flex items-start gap-3 p-4 bg-green-50 rounded-xl">
                <Check size={20} className="text-green-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-green-800">Import Complete</p>
                  <p className="text-sm text-green-700">{result.imported} records imported, {result.skipped || 0} skipped (duplicates)</p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3 p-4 bg-red-50 rounded-xl">
                <AlertTriangle size={20} className="text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800">Import Failed</p>
                  <p className="text-sm text-red-700">{result.error}</p>
                </div>
              </div>
            )}
            {result.errors?.length > 0 && (
              <div className="p-3 bg-yellow-50 rounded-xl">
                <p className="text-xs font-medium text-yellow-800 mb-1">Warnings:</p>
                {result.errors.map((err, i) => <p key={i} className="text-xs text-yellow-700">{err}</p>)}
              </div>
            )}
            <Button onClick={reset} variant="outline" className="rounded-xl">Import another file</Button>
          </div>
        )}
      </Card>

      {/* Expected format */}
      <Card className="p-4 rounded-2xl border">
        <h4 className="text-sm font-semibold mb-2" style={{ fontFamily: 'Playfair Display' }}>Expected CSV format for {currentType.label}</h4>
        <p className="text-xs text-muted-foreground mb-2">Your CSV should have a header row. Supported columns:</p>
        <div className="flex flex-wrap gap-1">
          {currentType.expectedFields.map(f => (
            <Badge key={f} variant="outline" className="text-xs rounded-full">{f}</Badge>
          ))}
        </div>
      </Card>
    </div>
  );
}
